import { Component, NgZone, OnDestroy, OnInit, TemplateRef, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';

import { AuthService } from '../../services';
import { AuthenticationResult, BrowserAuthErrorCodes } from '@azure/msal-browser';
import { LoaderService } from '../../services/loader.service';
import { MatDialogRef } from '@angular/material/dialog';
import { MsalService } from '@azure/msal-angular';
import { ActivatedRoute, Router } from '@angular/router';
import { SecurityProfileEnum } from '../../enums/securityProfile.enum';
import { ToastService } from '../../services/toast.service';
import { environment } from '../../../environments/environment';
import { Subject } from 'rxjs';
import { first, switchMap, takeUntil } from 'rxjs/operators';
import { RoutePaths } from 'src/app/shared/constants/routePaths.constants';

declare var google: any;

@Component({
  standalone: false,
  selector: 'app-login',
  templateUrl: './login.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./login.component.scss'],
})
export class LoginComponent implements OnInit, OnDestroy {

  loginForm: UntypedFormGroup;
  returnUrl: string;
  rememberMe = false;
  show: boolean;
  profile: Object;
  isLoading: boolean;
  otp: string;
  @ViewChild('callAPIDialog', { static: false }) callAPIDialog: TemplateRef<any>;
  public dialogRef: MatDialogRef<any>;
  showMFA: boolean;
  /** True when the current OTP challenge is the forced-enrolment case (role newly mandates MFA, user not yet enrolled). */
  isForcedEnrollment = false;
  MFAForm: UntypedFormGroup;
  userType: SecurityProfileEnum;
  isZendeskLogin: boolean;
  private destroy$ = new Subject<void>();

  constructor(
    private _fb: UntypedFormBuilder,
    private authService: AuthService,
    private msalAuthService: MsalService,
    private toastService: ToastService,
    private router: Router,
    private zone: NgZone,
    private loaderService: LoaderService,
    private activatedRoute: ActivatedRoute,
  ) {
    this.msalAuthService.initialize();
    this.rememberMe = this.authService.getRememberMe();
  }

  ngOnInit(): void {
    this.createForm();
    google.accounts.id.initialize({
      client_id: environment.googleClientId,
      callback: (response: any) => this.handleGoogleSignIn(response)
    });

    setTimeout(() => {
      google.accounts.id.renderButton(
        document.getElementById('googleBtnDiv'),
        {
          text: 'continue_with',
          size: 'large',
          type: 'standard',
          shape: 'rectangular',
          width: document.getElementById('googleBtnDiv')?.offsetWidth - 20,
          height: document.getElementById('googleBtnDiv')?.offsetHeight,
        }  // customization attributes
      );
    }, 50);

    this.activatedRoute.queryParams.pipe(takeUntil(this.destroy$)).subscribe(params => {
      this.isZendeskLogin = params['type'] && params['type'] === 'zendesk';
      if (params['returnUrl']) {
        this.returnUrl = params['returnUrl'];
      }
    });

    const currentUser = this.authService.getCurrentUser();
    if (this.authService.isLoggedIn() && currentUser) {
      if (this.isZendeskLogin) {
        this.authService.getZendeskSsoUrl().subscribe(url => this.redirectToZendesk(url));
      } else if (this.superAdmin) {
        this.navigateToOldDashboard();
      } else {
        this.navigateToHome();
      }
    } else {
      this.revokeTokens();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  rememberMeChanged(event) {
    this.rememberMe = event.target.checked;
  }

  handleGoogleSignIn(googleResponse) {
    this.loaderService.show();
    this.authService.googleLogin(googleResponse)
      .pipe(
        first(),
      ).subscribe({
        next: () => {
          this.loaderService.hide();
          this.onLoginSuccess()
        },
        error: (error) => {
          this.loaderService.hide();

          if (error.error && error.error instanceof Array && error.error.length > 0) {
            this.toastService.displayErrorMessage(error.error[0].description);
          }
        }
      });
  }

  createForm() {
    this.loginForm = this._fb.group({
      loginId: ['', [Validators.required, Validators.email]],
      password: ['', [Validators.required]]
    });
    this.MFAForm = this._fb.group({
      otp: ['', [Validators.required]]
    });
  }

  loginHandler() {
    if (this.loginForm.invalid) {
      const message = 'Please provide valid username and password';
      this.toastService.displayErrorMessage(message, true);
      // this.recordError(ErrorCodeEnum.Validation, message);
      return;
    }
    this.loginUser();
  }

  loginUser() {
    this.loaderService.show();
    this.authService.login(this.loginForm.controls.loginId.value, this.loginForm.controls.password.value)
      .pipe(first()).subscribe({
        next: () => {
          this.loaderService.hide();
          this.onLoginSuccess();
        },
        error: (error) => {
          this.loaderService.hide();
          if (error && error.status === 403 && (error.error === 'MFA' || error.error === 'MFA_ENROLLMENT_REQUIRED')) {
            this.handleMfa(error.error === 'MFA_ENROLLMENT_REQUIRED');
            return;
          }
          if (error.error && error.error instanceof Array && error.error.length > 0) {
            this.toastService.displayErrorMessage(error.error[0].description);
          }
        }
      });
  }

  onLoginSuccess() {
    this.authService.setRememberMe(this.rememberMe);
    if (this.isZendeskLogin) {
      this.authService.getZendeskSsoUrl().subscribe(url => this.redirectToZendesk(url));
    } else if (this.returnUrl) {
      this.router.navigateByUrl(this.returnUrl);
    } else {
      if (this.superAdmin) {
        this.navigateToOldDashboard();
      } else {
        this.navigateToHome();
      }
    }
    this.toastService.showSuccess('Logged in', true);
  }

  get superAdmin() {
    const currentUser = this.authService.getCurrentUser();
    if (currentUser && currentUser.userType) {
      this.userType = currentUser.userType;
    }
    return this.userType === SecurityProfileEnum.SuperAdmin;
  }

  handleMfa(isForcedEnrollment: boolean) {
    this.isForcedEnrollment = isForcedEnrollment;
    this.showMFA = true;
  }

  navigateToHome() {
    this.zone.run(() => {
      const landingPageUrl = this.authService.getLandingPageUrl();
      this.router.navigate([landingPageUrl]);
    });
  }

  navigateToOldDashboard() {
    this.zone.run(() => {
      this.router.navigate([RoutePaths.tenantSensorList]);
    });
  }

  revokeTokens() {
    if (this.authService.isLoggedIn()) {
      this.authService.revokeTokens()
        .subscribe(
          _ => _
        );
    }
  }

  navigateToForgotpassword() {
    this.router.navigate([RoutePaths.forgotPassword], { state: { email: this.loginForm.controls.loginId.value } });
  }

  loginWithMicrosoft() {
    // Keep the login page usable while the popup is open — the loader is only shown once the
    // popup has returned and the backend MicrosoftLogin call is in flight. MSAL v5 does not
    // reliably emit user_cancelled when the popup is closed, so a blocking loader here would
    // leave the page stuck until popupBridgeTimeout.
    let loaderShown = false;
    // Cancel any interaction left in progress by a previously closed popup — MSAL v5 keeps the
    // first popup's interaction lock after it is closed and otherwise throws interaction_in_progress
    // on every subsequent click for up to popupBridgeTimeout. Overriding it starts a fresh popup.
    this.msalAuthService.loginPopup({ scopes: [], overrideInteractionInProgress: true }).pipe(
      switchMap((result: AuthenticationResult) => {
        this.loaderService.show();
        loaderShown = true;
        return this.authService.microsoftLogin(result);
      })
    ).subscribe({
      next: () => {
        this.loaderService.hide();
        this.onLoginSuccess();
      },
      error: (error) => {
        // Only hide the loader if it was actually shown — popup-stage failures (user_cancelled,
        // popup/empty window) reject before the switchMap, so show() never ran.
        if (loaderShown) {
          this.loaderService.hide();
        }
        if (error?.error instanceof Array && error.error.length > 0) {
          this.toastService.displayErrorMessage(error.error[0].description);
          return;
        }
        // Closing the popup before finishing is a normal cancel, not a failure.
        // interaction_in_progress_cancelled is the old popup's subscription being superseded when
        // overrideInteractionInProgress starts a fresh popup — also a cancel, so keep it silent too.
        if (
          error?.errorCode === BrowserAuthErrorCodes.userCancelled ||
          error?.errorCode === BrowserAuthErrorCodes.interactionInProgressCancelled
        ) {
          return;
        }
        // The popup never opened because the browser blocked it — tell the user what to fix.
        if (
          error?.errorCode === BrowserAuthErrorCodes.popupWindowError ||
          error?.errorCode === BrowserAuthErrorCodes.emptyWindowError
        ) {
          this.toastService.displayErrorMessage('Please allow popups for this site to sign in with Microsoft.', true);
          return;
        }
        // Any other MSAL failure (e.g. bridge timeout) must surface instead of failing silently.
        this.toastService.displayErrorMessage('Microsoft sign-in failed. Please try again.', true);
      }
    });
  }

  MFASubmit() {
    this.isLoading = true;
    this.authService.verifyOtp(this.loginForm.value.loginId, this.MFAForm.value.otp).subscribe({
      next: () => {
        this.isLoading = false;
        this.toastService.showSuccess('OTP Code verified successfully', true);
        this.onLoginSuccess();
      },
      error: (error) => {
        this.isLoading = false;
        this.toastService.showError(error);
      }
    });
  }

  redirectToZendesk(url: string) {
    window.location.href = url;
  }

}
